import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Truck, CheckCircle2, Edit2, AlertCircle } from "lucide-react";

const SERVICE_LABELS = {
  towing: "Towing",
  jump_start: "Jump Start",
  tire_change: "Tire Change",
  fuel_delivery: "Fuel Delivery",
  lockout: "Lockout",
  winch_out: "Winch Out",
  ride_transport: "Ride / Transport",
};

export default function VehicleSetupPanel() {
  const { user, setUser } = useAuth();
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(!user?.vehicle_category);
  const [form, setForm] = useState({
    vehicle_category: user?.vehicle_category || "",
    vehicle_year: user?.vehicle_year || "",
    vehicle_make: user?.vehicle_make || "",
    vehicle_model: user?.vehicle_model || "",
    service_capabilities: user?.service_capabilities || [],
  });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/vehicle-categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const activeCat = cats.find((c) => c.id === form.vehicle_category);
  const availableSvcs = activeCat?.services || [];

  const toggleSvc = (s) => {
    setForm((f) => ({
      ...f,
      service_capabilities: f.service_capabilities.includes(s)
        ? f.service_capabilities.filter((x) => x !== s)
        : [...f.service_capabilities, s],
    }));
  };

  const pickCategory = (catId) => {
    const cat = cats.find((c) => c.id === catId);
    setForm({ ...form, vehicle_category: catId, service_capabilities: cat?.services || [] });
  };

  const save = async () => {
    setErr(""); setMsg("");
    if (!form.vehicle_category) { setErr("Please pick your vehicle type."); return; }
    if (form.service_capabilities.length === 0) { setErr("Pick at least one service you can perform."); return; }
    try {
      await api.put("/driver/vehicle", form);
      const me = await api.get("/auth/me");
      setUser(me.data);
      setMsg("Saved! You'll only see jobs your vehicle can handle.");
      setEditing(false);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const setup = !!user?.vehicle_category;

  return (
    <div className="bg-asap-blue/30 backdrop-blur border-2 border-asap-yellow/40 p-4" data-testid="vehicle-panel">
      <div className="text-[10px] uppercase tracking-[0.3em] text-asap-yellow font-bold flex items-center gap-1"><Truck size={12}/> Vehicle & Skills</div>

      {!editing && setup && (
        <>
          <div className="mt-2 flex justify-between items-start gap-3">
            <div>
              <div className="font-display text-lg text-asap-yellow leading-tight">{cats.find((c) => c.id === user.vehicle_category)?.label || user.vehicle_category}</div>
              {(user.vehicle_year || user.vehicle_make) && (
                <div className="text-xs text-white/70 mt-1">{[user.vehicle_year, user.vehicle_make, user.vehicle_model].filter(Boolean).join(" ")}</div>
              )}
            </div>
            <button onClick={() => setEditing(true)} data-testid="edit-vehicle-btn" className="p-2 border-2 border-asap-yellow text-asap-yellow hover:bg-asap-yellow hover:text-asap-deep shrink-0"><Edit2 size={14}/></button>
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-asap-yellow mb-1">You can perform:</div>
            <div className="flex flex-wrap gap-1">
              {(user.service_capabilities || []).map((s) => (
                <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-asap-yellow text-asap-deep text-[10px] uppercase tracking-widest font-bold" data-testid={`cap-${s}`}>
                  <CheckCircle2 size={10}/> {SERVICE_LABELS[s] || s}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {(editing || !setup) && (
        <div className="mt-3 space-y-3" data-testid="vehicle-form">
          {!setup && (
            <div className="p-2 bg-asap-red/20 border border-asap-red text-asap-yellow text-xs flex items-start gap-2">
              <AlertCircle size={14} className="shrink-0 mt-0.5"/>
              <span>Set your vehicle type before going online. This ensures you only get jobs your rig can handle.</span>
            </div>
          )}
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-asap-yellow mb-1">Vehicle Type</div>
            <select value={form.vehicle_category} onChange={(e) => pickCategory(e.target.value)} data-testid="vehicle-category" className="w-full px-3 py-2 bg-white/10 border border-asap-yellow/50 outline-none text-white text-sm">
              <option value="">-- pick one --</option>
              {cats.map((c) => (<option key={c.id} value={c.id} className="text-asap-deep">{c.label}</option>))}
            </select>
          </label>

          <div className="grid grid-cols-3 gap-2">
            <input placeholder="Year" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} data-testid="vehicle-year" className="px-2 py-2 bg-white/10 border border-asap-yellow/50 outline-none text-white text-sm placeholder:text-white/40"/>
            <input placeholder="Make" value={form.vehicle_make} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} data-testid="vehicle-make" className="px-2 py-2 bg-white/10 border border-asap-yellow/50 outline-none text-white text-sm placeholder:text-white/40"/>
            <input placeholder="Model" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} data-testid="vehicle-model" className="px-2 py-2 bg-white/10 border border-asap-yellow/50 outline-none text-white text-sm placeholder:text-white/40"/>
          </div>

          {activeCat && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-asap-yellow mb-1">Services you'll accept</div>
              <div className="grid grid-cols-2 gap-1.5">
                {availableSvcs.map((s) => {
                  const on = form.service_capabilities.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSvc(s)}
                      data-testid={`svc-toggle-${s}`}
                      className={`text-left px-2 py-1.5 text-xs uppercase tracking-widest font-bold border-2 transition ${on ? "bg-asap-yellow text-asap-deep border-asap-deep" : "bg-white/5 text-white/60 border-white/20"}`}
                    >
                      {on ? "✓ " : "○ "}{SERVICE_LABELS[s] || s}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-white/50 mt-1 italic">Only shown services your vehicle can physically handle.</div>
            </div>
          )}

          {err && <div className="text-asap-red text-xs font-bold">{err}</div>}
          {msg && <div className="text-emerald-300 text-xs font-bold">{msg}</div>}

          <div className="flex gap-2">
            <button onClick={save} data-testid="save-vehicle" className="px-3 py-1.5 bg-asap-yellow text-asap-deep font-display uppercase text-xs">Save</button>
            {setup && <button onClick={() => setEditing(false)} className="px-3 py-1.5 border border-white/40 text-white/70 text-xs">Cancel</button>}
          </div>
        </div>
      )}
    </div>
  );
}
